import { useCallback, useEffect, useRef, useState } from "react";
import Logger from "@/utility/logger";
import {
  convertToWav,
  createPcmRecorder,
  encodePcmToWav,
  type PcmRecorder,
} from "@/utility/audioWav";
import { startAudioLevelMeter } from "@/utility/audioLevelMeter";

/**
 * Composer dictation for the add-in's Chat With Documents.
 *
 * Mirrors the workspace's listening mode, scoped to one text field:
 *   - Hold Control (> HOLD_THRESHOLD_MS): push-to-talk; releasing commits.
 *   - Tap Control: hands-free listening; tap again to commit.
 *   - Escape cancels and restores the draft.
 *
 * Live text comes from Web Speech; when recording stops the captured audio is
 * re-transcribed through Whisper and replaces the live text. Whisper output
 * never overwrites text the user edited while it was in flight.
 *
 * Chords such as Ctrl+C / Ctrl+V cancel hold detection so shortcuts are never
 * hijacked.
 */

export type VoiceDictationStatus = "idle" | "listening" | "transcribing";
export type VoiceDictationActivation = "hold" | "tap" | "click";

const HOLD_THRESHOLD_MS = 140;
/** Clips shorter than this are noise clicks, not speech. */
const MIN_AUDIO_BYTES = 800;

const getSpeechRecognitionCtor = (): any => {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
};

const isDictationHardwareSupported = () =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

const countWords = (text: string) => text.split(/\s+/).filter(Boolean).length;

const joinDraft = (base: string, text: string) =>
  base ? `${base.replace(/\s+$/, "")} ${text}` : text;

const isControlKey = (e: KeyboardEvent) =>
  e.key === "Control" || e.code === "ControlLeft" || e.code === "ControlRight";

export interface UseVoiceDictationOptions {
  /** Current composer value; dictated text is appended to it. */
  value: string;
  onChange: (text: string) => void;
  /** Whisper transcription (POST /speech/transcribe). */
  transcribeAudio?: (audioBlob: Blob) => Promise<string>;
  /** Blocks starting (and cancels a running session) while true. */
  disabled?: boolean;
  /** Called once each time the microphone goes live. */
  onListeningStart?: () => void;
  lang?: string;
}

export interface UseVoiceDictationResult {
  status: VoiceDictationStatus;
  /** How the current session was started; drives the stop hint. */
  activation: VoiceDictationActivation | null;
  /** Microphone loudness, 0..1, while listening. */
  audioLevel: number;
  isSupported: boolean;
  error: string | null;
  /** Mic button: start when idle, commit when listening. */
  toggle: () => void;
  /** Commits the dictated text (runs the Whisper pass). */
  stop: () => void;
  /** Discards the session and restores the pre-dictation draft. */
  cancel: () => void;
}

export function useVoiceDictation({
  value,
  onChange,
  transcribeAudio,
  disabled = false,
  onListeningStart,
  lang = "en-US",
}: UseVoiceDictationOptions): UseVoiceDictationResult {
  const [status, setStatus] = useState<VoiceDictationStatus>("idle");
  const [activation, setActivation] = useState<VoiceDictationActivation | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSupported] = useState<boolean>(isDictationHardwareSupported);

  const statusRef = useRef(status);
  statusRef.current = status;
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const transcribeAudioRef = useRef(transcribeAudio);
  transcribeAudioRef.current = transcribeAudio;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onListeningStartRef = useRef(onListeningStart);
  onListeningStartRef.current = onListeningStart;

  const sessionRef = useRef(0);
  const startingRef = useRef(false);
  const pendingStopRef = useRef<"commit" | "cancel" | null>(null);
  const baseRef = useRef("");
  const committedRef = useRef("");
  const liveTextRef = useRef("");
  const recognitionRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const pcmRecorderRef = useRef<PcmRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopMeterRef = useRef<(() => void) | null>(null);

  const updateStatus = useCallback((next: VoiceDictationStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const stopMeter = useCallback(() => {
    stopMeterRef.current?.();
    stopMeterRef.current = null;
    setAudioLevel(0);
  }, []);

  const detachRecognition = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    try {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
    } catch {}
    recognitionRef.current = null;
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch {}
      streamRef.current = null;
    }
  }, []);

  /** Tears down every capture resource without touching the draft. */
  const teardown = useCallback(() => {
    detachRecognition();
    stopMeter();
    pcmRecorderRef.current?.dispose();
    pcmRecorderRef.current = null;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      } catch {}
    }
    recorderRef.current = null;
    releaseStream();
    chunksRef.current = [];
  }, [detachRecognition, releaseStream, stopMeter]);

  const cancel = useCallback(() => {
    if (startingRef.current) {
      pendingStopRef.current = "cancel";
      sessionRef.current += 1;
      return;
    }
    if (statusRef.current === "idle") return;
    sessionRef.current += 1;
    teardown();
    // Restore the draft only if the user has not typed over the live text.
    if (valueRef.current === committedRef.current) {
      onChangeRef.current(baseRef.current);
    }
    setActivation(null);
    updateStatus("idle");
  }, [teardown, updateStatus]);

  const refineWithWhisper = useCallback(
    async (session: number, chunks: Blob[]) => {
      try {
        const transcribe = transcribeAudioRef.current;
        if (!transcribe) return;
        const pcmSamples = pcmRecorderRef.current?.getSamples();
        const hasPcm = Boolean(pcmSamples && pcmSamples.length > 1600 && typeof encodePcmToWav === "function");
        if (!hasPcm && chunks.length === 0) return;

        let wav: Blob | null = null;
        if (hasPcm && pcmSamples) {
          wav = encodePcmToWav(pcmSamples, 16000);
        } else {
          const raw = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
          if (raw.size < MIN_AUDIO_BYTES) return;
          wav = await convertToWav(await raw.arrayBuffer());
        }

        if (!wav) return;
        const refined = (await transcribe(wav))?.trim();
        if (session !== sessionRef.current || !refined) return;

        // A single Whisper word must not replace a captured full sentence.
        if (countWords(liveTextRef.current) >= 3 && countWords(refined) <= 1) return;

        const current = valueRef.current;
        if (current === committedRef.current || current === baseRef.current) {
          const next = joinDraft(baseRef.current, refined);
          committedRef.current = next;
          onChangeRef.current(next);
        }
      } catch (err) {
        Logger.warn("useVoiceDictation: Whisper refinement failed:", err);
      } finally {
        if (session === sessionRef.current) {
          setActivation(null);
          updateStatus("idle");
        }
      }
    },
    [updateStatus]
  );

  const stop = useCallback(() => {
    if (startingRef.current) {
      pendingStopRef.current = "commit";
      return;
    }
    if (statusRef.current !== "listening") return;

    const session = sessionRef.current;
    updateStatus("transcribing");
    stopMeter();
    pcmRecorderRef.current?.stop();

    // Let the recognizer flush its last result; handlers detach on completion.
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch {}
    }

    const finalize = () => {
      releaseStream();
      recorderRef.current = null;
      const chunks = chunksRef.current;
      chunksRef.current = [];
      void refineWithWhisper(session, chunks).finally(() => {
        if (session === sessionRef.current) detachRecognition();
      });
    };

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      // MediaRecorder emits its final chunk before `stop`.
      recorder.onstop = finalize;
      try {
        recorder.stop();
      } catch {
        finalize();
      }
    } else {
      finalize();
    }
  }, [detachRecognition, refineWithWhisper, releaseStream, stopMeter, updateStatus]);

  const start = useCallback(
    async (how: VoiceDictationActivation) => {
      if (statusRef.current !== "idle" || startingRef.current || disabledRef.current) return;
      if (!isDictationHardwareSupported()) {
        setError("Dictation is not supported in this browser.");
        return;
      }

      startingRef.current = true;
      pendingStopRef.current = null;
      setError(null);
      sessionRef.current += 1;
      const session = sessionRef.current;

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch (err) {
        Logger.warn("useVoiceDictation: microphone permission denied:", err);
        startingRef.current = false;
        setError("Microphone access wasn't granted. Check your browser settings to allow access.");
        return;
      }

      startingRef.current = false;
      if (session !== sessionRef.current) {
        // Cancelled while the permission prompt was open.
        stream.getTracks().forEach((track) => track.stop());
        pendingStopRef.current = null;
        return;
      }

      streamRef.current = stream;
      pcmRecorderRef.current =
        typeof createPcmRecorder === "function" ? createPcmRecorder(stream) : null;
      pcmRecorderRef.current?.start();
      chunksRef.current = [];
      baseRef.current = valueRef.current;
      committedRef.current = valueRef.current;
      liveTextRef.current = "";

      try {
        let mimeType = "";
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) mimeType = "audio/webm;codecs=opus";
        else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) mimeType = "audio/ogg;codecs=opus";
        else if (MediaRecorder.isTypeSupported("audio/mp4")) mimeType = "audio/mp4";

        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorder.start(200);
        recorderRef.current = recorder;
      } catch (err) {
        Logger.warn("useVoiceDictation: MediaRecorder init failed:", err);
      }

      const SpeechRec = getSpeechRecognitionCtor();
      if (SpeechRec) {
        const recognition = new SpeechRec();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = lang;
        recognition.onresult = (event: any) => {
          if (session !== sessionRef.current) return;
          let text = "";
          for (let i = 0; i < event.results.length; i += 1) {
            const alt = event.results[i]?.[0];
            const piece = alt?.transcript?.trim();
            if (piece) text += (text ? " " : "") + piece;
          }
          if (!text) return;
          liveTextRef.current = text;
          const next = joinDraft(baseRef.current, text);
          committedRef.current = next;
          onChangeRef.current(next);
        };
        recognition.onerror = (event: any) => {
          if (event?.error !== "no-speech" && event?.error !== "aborted") {
            Logger.warn("useVoiceDictation: recognition error:", event?.error);
          }
        };
        recognition.onend = () => {
          // The browser ends continuous recognition on its own after a pause.
          if (session !== sessionRef.current || statusRef.current !== "listening") return;
          try {
            recognition.start();
          } catch {}
        };
        recognitionRef.current = recognition;
        try {
          recognition.start();
        } catch (err) {
          Logger.warn("useVoiceDictation: recognition.start failed:", err);
        }
      }

      stopMeterRef.current = startAudioLevelMeter(stream, setAudioLevel);
      setActivation(how);
      updateStatus("listening");
      onListeningStartRef.current?.();

      const pending = pendingStopRef.current;
      pendingStopRef.current = null;
      if (pending === "commit") stop();
      else if (pending === "cancel") cancel();
    },
    [cancel, lang, stop, updateStatus]
  );

  const startRef = useRef(start);
  startRef.current = start;
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;

  const toggle = useCallback(() => {
    if (statusRef.current === "listening") stop();
    else if (statusRef.current === "idle") void start("click");
  }, [start, stop]);

  // Hold / tap Control and Escape.
  useEffect(() => {
    let ctrlDownAt = 0;
    let holdTimer: ReturnType<typeof setTimeout> | null = null;
    let interrupted = false;
    let startedByHold = false;

    const clearHoldTimer = () => {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isControlKey(e)) {
        if (e.repeat) return;
        ctrlDownAt = Date.now();
        interrupted = false;
        startedByHold = false;
        clearHoldTimer();
        if (statusRef.current === "idle") {
          holdTimer = setTimeout(() => {
            holdTimer = null;
            if (interrupted) return;
            startedByHold = true;
            void startRef.current("hold");
          }, HOLD_THRESHOLD_MS);
        }
        return;
      }

      if (e.key === "Escape") {
        if (statusRef.current !== "idle") {
          e.preventDefault();
          clearHoldTimer();
          interrupted = true;
          startedByHold = false;
          cancelRef.current();
        }
        return;
      }

      // Any other key while Control is down is a shortcut, not push-to-talk.
      clearHoldTimer();
      interrupted = true;
      if (startedByHold && statusRef.current !== "idle") {
        startedByHold = false;
        cancelRef.current();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!isControlKey(e)) return;
      clearHoldTimer();

      if (!interrupted) {
        if (startedByHold || statusRef.current === "listening") {
          stopRef.current();
        } else if (statusRef.current === "idle" && Date.now() - ctrlDownAt < HOLD_THRESHOLD_MS) {
          void startRef.current("tap");
        }
      }
      startedByHold = false;
      interrupted = false;
    };

    // Control-up is never delivered once focus leaves the pane (e.g. into the
    // document), so a live mic would otherwise stay hot.
    const handleBlur = () => {
      clearHoldTimer();
      if (statusRef.current === "listening") stopRef.current();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      clearHoldTimer();
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // A parent that takes over the mic (voice mode, hidden tab, pending answer)
  // ends any running session.
  useEffect(() => {
    if (disabled) cancelRef.current();
  }, [disabled]);

  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      teardown();
    };
  }, [teardown]);

  return {
    status,
    activation,
    audioLevel: status === "listening" ? audioLevel : 0,
    isSupported,
    error,
    toggle,
    stop,
    cancel,
  };
}

export default useVoiceDictation;
